import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, IconButton, Modal } from '@mui/material';

import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../store/hooks';
import { metaDataSelector } from '../../store/globalSelectors';
import {
  setUlna,
  setEstimatedHeight,
  setEstBmiUlna
} from '../../store/slices/metaDataSlice';

import {
  getULNA_BMI,
  getULNA_height
} from '../../shared/utils/alternativeMeasures';
import { ImageModal } from '../../shared/components/modal/ImageModal';
import { SingleNumberCard } from '../../shared/components/numberInput/numberCards/singleNumberCard';
import InfoIcon from '../../assets/icons/infoIcon.svg';
import SlantArrowIcon from '../../assets/icons/slantArrowIcon.svg';
import BMIUlnaImage from '../../assets/images/BMI-UlnaLength.svg';
import bmiIconWhite from '../../assets/icons/bmiIconWhite.svg';
import { BMICard } from '../../shared/components/bmiCard';
import { AddOnInfoModal } from '../../shared/components/modal/addOnInfoModal';

export const Ulna = () => {
  const dispatch = useDispatch();
  const { alternativeMeasures, patientInformation } =
    useAppSelector(metaDataSelector);
  const { ulnaLength, estHeightUlna, estBMI_ulna } = alternativeMeasures;
  const [openImageModal, setOpenImageModal] = useState(false);
  const [openInfoModal, setOpenInfoModal] = useState(false);
  const { metric, age, gender } = patientInformation;
  const { weight, estimatedDryWeight } = metric;
  const { years: ageOfPatient } = age;

  useEffect(() => {
    if (ulnaLength < 18.5 || ulnaLength > 32) {
      dispatch(setEstimatedHeight('NA'));
      return;
    }

    const calculatedEstimatedHeight: number | string = getULNA_height(
      ulnaLength,
      gender.toLowerCase(),
      ageOfPatient
    );

    if (typeof calculatedEstimatedHeight !== 'string') {
      dispatch(setEstimatedHeight(calculatedEstimatedHeight));
    } else {
      dispatch(setEstimatedHeight(0));
    }
  }, [ulnaLength]);

  useEffect(() => {
    if (estHeightUlna > 0) {
      const consideredWeight =
        estimatedDryWeight > 0 ? estimatedDryWeight : weight;
      const calculatedEstBmi: string = getULNA_BMI(
        estHeightUlna,
        consideredWeight
      );
      dispatch(setEstBmiUlna(calculatedEstBmi));
    } else {
      dispatch(setEstBmiUlna('NA'));
    }
  }, [estHeightUlna]);

  return (
    <>
      <Box
        sx={{
          maxWidth: 600,
          margin: '0 auto',
          padding: 2,
          borderRadius: 0,
          backgroundColor: '#f9f9f9',
          marginTop: '-19px'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontFamily: 'Calibri',
              fontSize: '1rem',
              fontStyle: 'normal',
              fontWeight: 700,
              lineHeight: '1.25rem',
              textAlign: 'left'
            }}
          >
            Estimating height from ulna length{' '}
          </Typography>
          <IconButton
            onClick={() => setOpenInfoModal(true)}
            size="small"
            aria-label="info"
            data-testid="ulnaInfoPopupButton"
          >
            <img src={InfoIcon} alt="Info" width="20" height="20" />
          </IconButton>
        </Box>

        <Typography
          variant="h6"
          gutterBottom
          sx={{
            fontFamily: 'Calibri',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '1.25rem',
            textAlign: 'left'
          }}
        >
          Measure length of ulna in cm to estimate height in m.
        </Typography>
        <ol
          style={{
            fontFamily: 'Calibri',
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: '1.25rem',
            textAlign: 'left',
            marginTop: '1.25rem',
            paddingLeft: '0.9375rem'
          }}
        >
          <li>
            Bend arm (left side if possible), place palm of hand across chest
            with fingers pointing to opposite shoulder.
          </li>
          <li>
            Using a tape measure, measure the length in centimeters (cm) to the
            nearset 0.5cm between the point of the elbow (olecranon) and the
            mid-point of the prominent bone on the wrist (ulna styloid process).
          </li>
          <li> Record ulna length below.</li>
        </ol>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '1.25rem'
          }}
        >
          <Button
            variant="outlined"
            sx={{
              marginBottom: 2,
              height: '2.5rem',
              padding: '0 0.9375rem',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              textAlign: 'center',
              lineHeight: 'normal',
              borderRadius: '100px',
              backgroundColor: '#007ACC',
              color: '#FFF'
            }}
            onClick={() => setOpenImageModal(true)}
            data-testid="ulnaImagePopupButton"
          >
            Click for image
            <Box
              sx={{
                height: '1.25rem',
                width: '1.25rem',
                backgroundColor: '#FFF',
                borderRadius: '50%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: '0.625rem'
              }}
            >
              <img
                src={SlantArrowIcon} // Replace with the actual path to your image
                alt="icon"
                style={{
                  height: '60%',
                  width: '60%',
                  objectFit: 'contain'
                }}
              />
            </Box>
          </Button>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <SingleNumberCard
            width={'50%'}
            title={'ULNA LENGTH'}
            marginVal={'0.625rem'}
            subHead={'(cm)'}
            value={Number(ulnaLength)}
            setValue={(val: number) => dispatch(setUlna(Number(val)))}
          />
        </Box>

        <Box
          sx={{
            display: 'flex',
            padding: '0.625rem 0.9375rem',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '0.625rem',
            height: '65px',
            alignSelf: 'stretch',
            backgroundColor: '#5BC2E740',
            color: '#002A3A',
            borderRadius: '0.9375rem',
            margin: '1.25rem'
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Brandon Grotesque',
              fontWeight: 450,
              fontSize: '1.5rem'
            }}
          >
            Estimated height (m):{' '}
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Calibri',
              fontWeight: 700,
              fontSize: '1.875rem'
            }}
          >
            <span data-testid="estHeightUlnaTest">{estHeightUlna}</span>
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <BMICard
            icon={bmiIconWhite}
            label1="Estimated BMI"
            label2={String(estBMI_ulna)}
            unit={' kg/m<sup>2</sup>'}
          />
        </Box>
      </Box>
      <ImageModal
        open={openImageModal}
        onClose={() => setOpenImageModal(false)}
        srcImage={BMIUlnaImage}
        imageHeight="auto"
        dialogPadding="0.3125rem"
      />
      <Modal
        open={openInfoModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AddOnInfoModal
          heading1={`BMI`}
          description1={` = Body Mass Index`}
          title={`Reference:`}
          content={`Irish Nutrition and Dietetic Institute (2022) Nutrition Support
        Reference Guide.`}
          onClose={() => setOpenInfoModal(false)}
        />
      </Modal>
    </>
  );
};
